import React from "react";
import {
  authMiddleware,
  getCurrentUser,
  loginMoodle /* requestAutoLogout */,
} from "../services/moodle/userauth";
import { STRINGS } from "../utils/strings";
import dic from "../utils/languages.json";
import { useConfig } from "./config";
import { isProductionAula, WORDPRESS_LOGIN_URL } from "../utils/wordpressLogin";

export const AuthContext = React.createContext();

const appendTokenToUrl = (url, token) => {
  if (!url) return url;

  const parsedUrl = new URL(url);

  parsedUrl.pathname = parsedUrl.pathname.replace(
    "/pluginfile.php/",
    "/webservice/pluginfile.php/"
  );

  parsedUrl.searchParams.set("token", token);

  return parsedUrl.toString();
};

/* actions to work on */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = React.useState(null);
  const { config } = useConfig();
  const [verified, setVerified] = React.useState(false);
  const [lang, setLang] = React.useState(null);
  const [apiToken, setApiToken] = React.useState(
    () => localStorage.getItem("api_token") || null
  );
  const [wpUrl, setWpUrl] = React.useState(
    () => localStorage.getItem("wp_url") || null
  );

  const actions = {
    RefreshAuth: async () => {
      try {
        const token = localStorage.getItem("token");
        const username = localStorage.getItem("username");
        //console.log({ token, username });
        const user = await getCurrentUser({ token, username, baseUrl: config.baseURL });
        setUser({
          ...user,
          profileimageurl: appendTokenToUrl(user.profileimageurl, user.token),
          profileimageurlsmall: appendTokenToUrl(user.profileimageurlsmall, user.token),
        });
        setVerified(true);
      } catch (e) {
        console.error("RefreshAuth error, clearing local session:", e);
        localStorage.removeItem("token");
        localStorage.removeItem("username");
        localStorage.removeItem("refresh");
        setUser(null);
        setVerified(true);
        throw new Error(e);
      }
    },
    Authenticate: async () => {
      if (localStorage.getItem("token") && !user) {
        /*         console.log('refreshtoken') */
        return Promise.resolve(await actions.RefreshAuth());
      }
      setVerified(true);
    },
    /**
     * Bootstrap an authenticated session from an existing token (e.g. SSO handoff).
     */
    bootstrapSession: async ({ token, username, apiToken: newApiToken, wpUrl: newWpUrl }) => {
      const user = await getCurrentUser({ token, username, baseUrl: config.baseURL });
      const enrichedUser = {
        ...user,
        profileimageurl: appendTokenToUrl(user.profileimageurl, user.token),
        profileimageurlsmall: appendTokenToUrl(user.profileimageurlsmall, user.token),
      };
      setUser(enrichedUser);
      setVerified(true);
      localStorage.setItem("token", token);
      localStorage.setItem("username", username);

      // api_token/wp_url son opcionales: solo vienen cuando WordPress inició
      // el SSO (no en demo mode manual sin esos parámetros). Sin ellos,
      // MiCuenta simplemente no puede pedir datos a WordPress.
      if (newApiToken && newWpUrl) {
        localStorage.setItem("api_token", newApiToken);
        localStorage.setItem("wp_url", newWpUrl);
        setApiToken(newApiToken);
        setWpUrl(newWpUrl);
      }

      return enrichedUser;
    },
    Login: async function (credentials) {
      try {
        const moodleUserInfo = await loginMoodle({credentials, baseUrl: config.baseURL,});
        /* const moodleUserInfo = await authMiddleware({
          token,
          username: credentials.username,
        }); */
        setUser({
          ...moodleUserInfo,
          profileimageurl: appendTokenToUrl(moodleUserInfo.profileimageurl, moodleUserInfo.token),
          profileimageurlsmall: appendTokenToUrl(moodleUserInfo.profileimageurlsmall, moodleUserInfo.token),
        });
        setVerified(true);

        if (moodleUserInfo.errorcode === STRINGS.FORCEPASSWORD_ERROR_CODE) {
          localStorage.setItem(
            STRINGS.FORCEPASSWORD_ERROR_CODE,
            STRINGS.FORCEPASSWORD_ERROR_CODE
          );
        }
      } catch (e) {
        throw new Error(e.message);
      }
    },
    login: () => window.location.replace("/"),
    logout: async (moodle = null, { redirigirExterno = false } = {}) => {
      if (user && user.id) {
        localStorage.setItem("newaccess", user.id);
      }
      if (moodle) {
        if (moodle.categories || moodle.courses || moodle.grades) {
          moodle.setCourses(null);
          moodle.setCategories(null);
          moodle.setGrades(null);
          moodle.setCalendarEvents(null);
        }
      }
      localStorage.removeItem(STRINGS.FORCEPASSWORD_ERROR_CODE);
      localStorage.removeItem("token");
      localStorage.removeItem("refresh");
      localStorage.removeItem("username");
      localStorage.removeItem("api_token");
      localStorage.removeItem("wp_url");
      localStorage.removeItem("last-access-cmid");
      localStorage.removeItem("sence_session");
      localStorage.removeItem("current_course");
      localStorage.removeItem("last_login_sence");
      localStorage.removeItem("darkMode");
      localStorage.removeItem("idSence");
      localStorage.removeItem("nru");
      localStorage.removeItem("sence_validated");

      // En producción, cerrar también la sesión WordPress aunque esta sesión
      // React no tenga wp_url (por ejemplo, tras una recarga o login directo).
      // Redirigir antes de mutar el estado `user` de React. Si ejecutamos `setUser(null)`
      // antes, ProtectedRoute detecta la sesión cerrada y hace <Navigate to="/" />,
      // mostrando la pantalla de login interna de React durante el parpadeo
      // previo a que el browser cargue la URL externa.
      if (redirigirExterno && isProductionAula()) {
        window.location.replace(`${WORDPRESS_LOGIN_URL}?logout=1`);
        return true;
      }

      setUser(null);
      setApiToken(null);
      setWpUrl(null);
      return false;
    },

    fetchDictionary: async () => {
      let url = window.location.href;
      let l = "";
      /* insertar funcion que maneje el idioma */
      if (url.includes("academy.tuclase")) {
        l = "en";
      } else {
        l = "es";
      }
      //console.log(dic[l])
      setLang(dic[l]);
    },
  };

  const value = {
    user,
    setUser,
    verified,
    lang,
    apiToken,
    wpUrl,
    Authenticate: actions.Authenticate,
    Login: actions.Login,
    logout: actions.logout,
    login: actions.login,
    bootstrapSession: actions.bootstrapSession,
    dictionary: actions.fetchDictionary,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => React.useContext(AuthContext);
